import React, { useState } from 'react';
import { Badge } from './ui/badge';
import { ChevronDown, ChevronRight, MessageSquare, Clock } from 'lucide-react';

const STATUS_META = {
    present: { label: 'Present', cls: 'bg-[#ECFDF5] text-[#047857] border-[#A7F3D0]' },
    late: { label: 'Late', cls: 'bg-[#FFFBEB] text-[#B45309] border-[#FDE68A]' },
    left_early: { label: 'Left early', cls: 'bg-[#F5F3FF] text-[#6D28D9] border-[#DDD6FE]' },
    absent: { label: 'Absent', cls: 'bg-[#FEF2F2] text-[#DC2626] border-[#FECACA]' },
};
const fmtDay = (d) => new Date(`${d}T12:00:00`).toLocaleDateString('en-IE', { weekday: 'short', day: '2-digit', month: 'short', year: 'numeric' });

const Count = ({ n, tone, testId }) => <span className={`tabular-nums font-semibold ${n ? tone : 'text-[#9CA3AF]'}`} data-testid={testId}>{n}</span>;

const DayList = ({ records }) => (
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-1.5 p-3 bg-[#FAF9F6]">
        {records.length === 0 && <p className="text-sm text-[#6B7280] col-span-full">No attendance recorded in this period.</p>}
        {records.map(r => (
            <div key={r.date} className={`flex items-center justify-between rounded border px-2.5 py-1.5 text-xs ${STATUS_META[r.status]?.cls || 'bg-white'}`} data-testid={`att-day-${r.date}`}>
                <span className="font-medium">{fmtDay(r.date)}</span>
                <span className="flex items-center gap-2">
                    {r.marked_at && <span className="inline-flex items-center gap-0.5 opacity-80"><Clock className="h-3 w-3" />{r.marked_at}</span>}
                    {r.late_sms_sent && <MessageSquare className="h-3 w-3" title="Late SMS sent to guardians" />}
                    <span className="uppercase tracking-wide">{STATUS_META[r.status]?.label || r.status}</span>
                </span>
            </div>
        ))}
    </div>
);

export const AttendanceHistoryTable = ({ students, showClass = true, inlineClass = true }) => {
    const [open, setOpen] = useState({});
    if (!students.length) return <p className="p-8 text-center text-sm text-[#6B7280]" data-testid="att-history-empty">No students match.</p>;
    return (
        <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="att-history-table">
                <thead className="text-xs uppercase text-[#6B7280] bg-[#F9FAFB]">
                    <tr>
                        <th className="text-left px-4 py-2 font-medium">Student</th>
                        {showClass && <th className="text-left px-4 py-2 font-medium">Class</th>}
                        <th className="text-center px-3 py-2 font-medium">Days</th>
                        <th className="text-center px-3 py-2 font-medium text-[#047857]">Present</th>
                        <th className="text-center px-3 py-2 font-medium text-[#B45309]">Late</th>
                        <th className="text-center px-3 py-2 font-medium text-[#6D28D9]">Left early</th>
                        <th className="text-center px-3 py-2 font-medium text-[#DC2626]">Absent</th>
                        <th className="text-center px-3 py-2 font-medium">Attendance</th>
                    </tr>
                </thead>
                <tbody>
                    {students.map(s => (
                        <React.Fragment key={s.id}>
                            <tr className="border-t hover:bg-[#F0FDF4] cursor-pointer" onClick={() => setOpen(o => ({ ...o, [s.id]: !o[s.id] }))} data-testid={`att-history-row-${s.id}`}>
                                <td className="px-4 py-2.5">
                                    <div className="flex items-center gap-2">
                                        {open[s.id] ? <ChevronDown className="h-4 w-4 text-[#6B7280]" /> : <ChevronRight className="h-4 w-4 text-[#6B7280]" />}
                                        <div><p className="font-medium text-[#1F2937]">{s.name} {s.arabic_name && <span className="text-xs text-[#6B7280] ml-1" dir="rtl">{s.arabic_name}</span>}</p>
                                            <p className="text-xs text-[#6B7280] font-mono">{[s.reg_code, (!showClass && inlineClass) ? s.class_name : ''].filter(Boolean).join(' · ')}</p></div>
                                    </div>
                                </td>
                                {showClass && <td className="px-4 py-2.5 text-xs text-[#4B5563]">{s.class_name}{s.arabic_class && <span className="ml-1" dir="rtl">{s.arabic_class}</span>}<br /><span className="text-[#9CA3AF]">{s.program_name}</span></td>}
                                <td className="text-center px-3 py-2.5 tabular-nums">{s.total}</td>
                                <td className="text-center px-3 py-2.5"><Count n={s.counts.present} tone="text-[#047857]" testId={`att-present-count-${s.id}`} /></td>
                                <td className="text-center px-3 py-2.5"><Count n={s.counts.late} tone="text-[#B45309]" testId={`att-late-count-${s.id}`} />{s.late_sms > 0 && <span className="ml-1 text-[10px] text-[#0F766E]" title="Late SMS sent">({s.late_sms} SMS)</span>}</td>
                                <td className="text-center px-3 py-2.5"><Count n={s.counts.left_early} tone="text-[#6D28D9]" /></td>
                                <td className="text-center px-3 py-2.5"><Count n={s.counts.absent} tone="text-[#DC2626]" testId={`att-absent-count-${s.id}`} /></td>
                                <td className="text-center px-3 py-2.5">{s.attendance_pct === null ? <span className="text-[#9CA3AF]">—</span>
                                    : <Badge className={`${s.attendance_pct >= 90 ? 'bg-[#047857]' : s.attendance_pct >= 75 ? 'bg-[#D97706]' : 'bg-[#DC2626]'} text-white`} data-testid={`att-pct-${s.id}`}>{s.attendance_pct}%</Badge>}</td>
                            </tr>
                            {open[s.id] && <tr className="border-t"><td colSpan={showClass ? 8 : 7} className="p-0"><DayList records={s.records} /></td></tr>}
                        </React.Fragment>
                    ))}
                </tbody>
            </table>
        </div>
    );
};
